/*!
 * Copyright © 2018-2021. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { isElementNull } from '../../../../utils';
import {
  ICreateGeofenceParams,
  IEditAssetParams,
  IEditDriverParams,
  IEditPlaceParams,
  IEditVehicleParams,
  IFindDirectionParams,
  IFindNearestParams,
  IReplayWindowParameters,
  IViewGarminWindowParams,
  IWorkOrderParams
} from '../../models';
import { IModalWindowDimensions } from './modal-window-dimensions.interface';
import { IModalWindowService } from './modal-window.service.interface';

const SILENT_REFRESH_TIMEOUT = 2000;

@Injectable()
export class ModalWindowService implements IModalWindowService {
  private readonly _rootUrl: string;

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {
    this._rootUrl = environment.config.legacyBaseUrl;
  }

  public openWithPostCentered(
    url: string,
    name: string,
    params?:
      | ICreateGeofenceParams
      | IFindNearestParams
      | IFindDirectionParams
      | IReplayWindowParameters
      | IEditDriverParams
      | IEditVehicleParams
      | IViewGarminWindowParams
      | IEditPlaceParams
      | IWorkOrderParams,
    shouldScaleModal?: boolean,
    useSilentRefresh = false
  ): void {
    this.openWithPost(url, name, params, shouldScaleModal, false, useSilentRefresh);
  }

  public async openWithPost(
    url: string,
    name: string,
    params?:
      | ICreateGeofenceParams
      | IFindNearestParams
      | IFindDirectionParams
      | IReplayWindowParameters
      | IEditDriverParams
      | IEditVehicleParams
      | IViewGarminWindowParams
      | IEditPlaceParams
      | IWorkOrderParams,
    shouldScaleModal?: boolean,
    isOpenAsTab: boolean = true,
    useSilentRefresh = false
  ): Promise<void> {
    if (useSilentRefresh) {
      await this._tryRefreshToken(url, params);
    }

    let windowFeatures: string;
    if (!isOpenAsTab) {
      const { width, height, top, left } = this._getDimensions(this._document.defaultView, this._document, shouldScaleModal);
      windowFeatures = `scrollbars=yes, width=${width}, height=${height}, top=${top}, left=${left}`;
    }
    const form = this._document.createElement('form');
    this._setForm(form, `${this._rootUrl}${url}`, name, params);

    this._document.body.appendChild(form);
    const newWindow = this._document.defaultView.open('about:blank', name, windowFeatures);
    form.submit();
    this._document.body.removeChild(form);

    if (newWindow) {
      newWindow.focus();
    }
  }

  public openWithPostCenteredAsync(
    url: string,
    name: string,
    params$: Observable<IReplayWindowParameters>,
    shouldScaleModal?: boolean
  ): void {
    const { width, height, top, left } = this._getDimensions(this._document.defaultView, this._document, shouldScaleModal);
    const windowFeatures = `scrollbars=yes, width=${width}, height=${height}, top=${top}, left=${left}`;
    const newWindow = this._document.defaultView.open('about:blank', name, windowFeatures);
    params$
      .pipe(
        catchError(() => {
          newWindow.close();
          return of(null);
        }),
        filter(params => !isElementNull(params)),
        take(1)
      )
      .subscribe(params => {
        const form = this._document.createElement('form');
        this._setForm(form, `${this._rootUrl}${url}`, name, params);
        this._document.body.appendChild(form);
        form.submit();
        this._document.body.removeChild(form);

        if (newWindow) {
          newWindow.focus();
        }
      });
  }

  public redirectToUrl(url: string): void {
    this._document.defaultView.location.href = `${this._rootUrl}${url}`;
  }

  public redirectWithPost(url: string, name: string, params?: IEditDriverParams | IEditVehicleParams | IEditAssetParams): void {
    const form = this._document.createElement('form');
    this._setForm(form, `${this._rootUrl}${url}`, name, params);

    this._document.body.appendChild(form);
    form.submit();
    this._document.body.removeChild(form);
  }

  public openNewTabRelative(url: string, target: string) {
    this.openNewTab(`${this._rootUrl}${url}`, target);
  }

  public openNewTab(url: string, target: string): void {
    this._document.defaultView.open(url, target);
  }

  public openNewTabWithMessage(url: string, target: string, message: unknown): void {
    const newTab = this._document.defaultView.open(url, target);
    const targetOrigin = new URL(url).origin;

    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handleReady);
    }, 30000);

    function handleReady(event: MessageEvent) {
      if (event.origin !== targetOrigin) {
        return;
      }
      if (event.source === newTab && event.data === 'reports-ready-for-message') {
        newTab?.postMessage(message, targetOrigin);
        window.removeEventListener('message', handleReady);
        clearTimeout(timeoutId);
      }
    }

    window.addEventListener('message', handleReady);
  }

  public openInANewWindowSpecificSize(url: string, name: string, shouldScaleModal?: boolean): void {
    const { width, height, top, left } = this._getDimensions(this._document.defaultView, this._document, shouldScaleModal);
    const windowFeatures = `scrollbars=yes, width=${width}, height=${height}, top=${top}, left=${left}`;

    const newWindow = this._document.defaultView.open(url, name, windowFeatures);

    if (newWindow) {
      newWindow.focus();
    }
  }

  private _setForm(form: HTMLFormElement, url: string, name: string, params: any) {
    form.setAttribute('method', 'post');
    form.setAttribute('action', url);
    form.setAttribute('target', name);

    for (const i in params) {
      if (params.hasOwnProperty(i)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = i;
        input.value = params[i];
        form.appendChild(input);
      }
    }
  }

  private _getDimensions(nativeWindow: Window, nativeDocument: Document, shouldScaleModal: boolean = false): IModalWindowDimensions {
    let width = 1068;
    let height = 667;
    const division = 2;
    const nativeWidth = nativeWindow.screen.width;
    const nativeHeight = nativeWindow.screen.height;
    const nativeLeft = !isElementNull(nativeWindow.screenLeft) ? nativeWindow.screenLeft : nativeWindow.screenX;
    const nativeTop = !isElementNull(nativeWindow.screenTop) ? nativeWindow.screenTop : nativeWindow.screenY;

    if (shouldScaleModal) {
      if (nativeWidth <= 1024) {
        width = nativeWidth - 120;
        height = nativeHeight - 200;
      } else if (nativeWidth <= 1280) {
        width = nativeWidth - 120;
        height = nativeHeight - 215;
      } else if (nativeWidth === 1920) {
        width = nativeWidth - 175;
        height = nativeHeight - 250;
      } else {
        width = nativeWidth - 170;
        height = nativeHeight - 225;
      }
    }
    const marginLeft = nativeWidth / division - width / division + nativeLeft;
    const marginTop = nativeHeight / division - height / division + nativeTop;

    return {
      width,
      height,
      left: marginLeft,
      top: marginTop
    };
  }

  private async _tryRefreshToken(
    url: string,
    params?:
      | ICreateGeofenceParams
      | IFindNearestParams
      | IFindDirectionParams
      | IReplayWindowParameters
      | IEditDriverParams
      | IEditVehicleParams
      | IViewGarminWindowParams
      | IEditPlaceParams
      | IWorkOrderParams
  ): Promise<void> {
    try {
      await this._performSilentRefresh(`${this._rootUrl}${url}`, params);
    } catch (error) {
      // Do not break the flow because of token refresh error
    }
  }

  private _performSilentRefresh(url: string, params: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const hiddenIframeName = 'silent-refresh-frame';
      const hiddenIframe = this._document.createElement('iframe');
      hiddenIframe.name = hiddenIframeName;
      hiddenIframe.style.display = 'none';

      const timeoutId = setTimeout(() => {
        // This will run only if the onload event doesn't fire in time
        if (hiddenIframe.parentElement) {
          hiddenIframe.parentElement.removeChild(hiddenIframe);
        }
        reject(new Error('Silent refresh operation timed out.'));
      }, SILENT_REFRESH_TIMEOUT);

      // The first onload fires when the hidden iframe is added and loads an about:blank page
      hiddenIframe.onload = () => {
        // The second onload fires after the frame has loaded the actual content
        hiddenIframe.onload = () => {
          // Clear the timeout so it doesn't fire in case of success
          clearTimeout(timeoutId);
          if (hiddenIframe.parentElement) {
            hiddenIframe.parentElement.removeChild(hiddenIframe);
          }
          resolve();
        };

        // Create and submit the form to start loading the page
        const form = this._document.createElement('form');
        this._setForm(form, url, hiddenIframeName, params);
        this._document.body.appendChild(form);
        form.submit();
        this._document.body.removeChild(form);
      };

      this._document.body.appendChild(hiddenIframe);
    });
  }
}
